import React, { Suspense, useEffect } from 'react';
import { createBrowserRouter, createHashRouter, Navigate, RouterProvider } from 'react-router-dom';
import { latestBlockAtom } from './atoms';

// Static imports for shell components (needed immediately on every page)
import Root from './components/Root';
import { PageLoadingSkeleton, GenericError } from './components/shared';

// Route-based code splitting — each route is loaded on demand
const Dashboard = React.lazy(() => import('./components/Products/Dashboard'));
const Uno = React.lazy(() => import('./components/Products/Uno'));
const LendAndBorrow = React.lazy(() => import('./components/Products/LendAndBorrow'));
const LendAndBorrowActionResolver = React.lazy(() =>
  import('./components/Products/LendAndBorrow').then(m => ({ default: m.LendAndBorrowActionResolver })),
);
const Pools = React.lazy(() =>
  import('./components/Products/Pools').then(m => ({ default: m.Pools })),
);
const PoolActionResolver = React.lazy(() =>
  import('./components/Products/Pools').then(m => ({ default: m.PoolActionResolver })),
);
const Stake = React.lazy(() => import('./components/Products/Stake'));
const SwapResolver = React.lazy(() => import('./components/Products/Swap'));
const Claim = React.lazy(() => import('./components/Products/Claim'));
const Points = React.lazy(() => import('./components/Products/Points'));
const Airdrop = React.lazy(() => import('./components/Products/Airdrop'));
const Bridge = React.lazy(() => import('./components/Products/Bridge'));
const Changelog = React.lazy(() => import('./components/Products/Changelog/Changelog'));
const Analytics = React.lazy(() => import('./components/Products/Analytics'));

const createRouter = import.meta.env.MODE === 'ipfs' ? createHashRouter : createBrowserRouter;

const router = createRouter(
  [
  {
    path: '/',
    errorElement: <GenericError />,
    element: <Root />,
    children: [
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
      {
        path: 'lend-borrow/*',
        element: <Navigate to="/lend-borrow" replace />,
      },
      {
        path: 'pools/*',
        element: <Navigate to="/pools" replace />,
      },
      {
        path: '/',
        errorElement: <GenericError />,
        element: (
          <Suspense>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: 'uno',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Uno />
          </Suspense>
        ),
      },
      {
        path: 'lend-borrow',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <LendAndBorrow />
          </Suspense>
        ),
      },
      {
        path: 'lend-borrow/:ticker/:action',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <LendAndBorrowActionResolver />
          </Suspense>
        ),
      },
      {
        path: 'pools',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Pools />
          </Suspense>
        ),
      },
      {
        path: 'pools/:pair/:action',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <PoolActionResolver />
          </Suspense>
        ),
      },
      {
        path: 'stake',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Stake />
          </Suspense>
        ),
      },
      {
        path: 'swap/:fromToken?/:toToken?/:amount?',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <SwapResolver />
          </Suspense>
        ),
      },
      {
        path: 'claim',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Claim />
          </Suspense>
        ),
      },
      {
        path: 'points',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Points />
          </Suspense>
        ),
      },
      {
        path: 'airdrop',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Airdrop />
          </Suspense>
        ),
      },
      {
        path: 'bridge',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Bridge />
          </Suspense>
        ),
      },
      {
        path: 'changelog',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Changelog />
          </Suspense>
        ),
      },
      {
        path: 'analytics',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Analytics page="lend-borrow" />
          </Suspense>
        ),
      },
      {
        path: 'analytics/lend-borrow',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Analytics page="lend-borrow" />
          </Suspense>
        ),
      },
      {
        path: 'analytics/lend-borrow/:asset',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Analytics page="lend-borrow" />
          </Suspense>
        ),
      },
      {
        path: 'analytics/pools',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Analytics page="pools" />
          </Suspense>
        ),
      },
      {
        path: 'analytics/pools/:poolId',
        errorElement: <GenericError />,
        element: (
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Analytics page="pools" />
          </Suspense>
        ),
      },
    ],
  },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

// Eagerly start the RPC fetch at module load — non-blocking, so the app
// renders immediately while the block number resolves in the background.
latestBlockAtom.getValue();

function App() {
  // Re-trigger if the module-level call hasn't completed yet (e.g. HMR).
  useEffect(() => {
    latestBlockAtom.getValue();
  }, []);

  return (
    <React.StrictMode>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </React.StrictMode>
  );
}

export default App;
